import { c as _c } from "react/compiler-runtime";
import { RoleChangeSuccessDescription } from "@components/table/members/members-column/role-column/role-change-success-description";
import { RolePartiallyChanged } from "@components/table/members/members-column/role-column/role-partially-changed";
import { useToast } from "@flat-ui";
import { useConfirmSelfRoleChange, useSelfProjectAccess } from "@hooks/projects/use-self-project-access";
import { updateMemberRoleInGroup } from "@store/groups/groups-slice-thunk";
import { updateMemberRoleInWorkspace } from "@store/members/members-slice-thunk";
import { updateMemberRoleInProjectSingle } from "@store/projects/projects-slice-thunk";
import { useAppDispatch } from "@store/store-helper";
import { formatUserRoleType } from "@utils/data-display";
import { GroupMemberEvents, ProjectMemberEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
/**
 * Hook for changing a single member's role.
 * Dispatches the appropriate thunk based on subject type and shows toast on success.
 * Reusable across different UI patterns (table cell dropdown, 3-dot menu, etc.).
 */
export function useChangeMemberRole(t0) {
  const $ = _c(16);
  const {
    companyId,
    subjectType,
    memberIdentity,
    currentRole,
    selectedProject,
    group,
    location
  } = t0;
  const dispatch = useAppDispatch();
  const {
    openToast
  } = useToast();
  const {
    trackEvent
  } = useTrackEvent();
  const t1 = selectedProject?.id ?? "";
  let t2;
  if ($[0] !== companyId || $[1] !== t1) {
    t2 = {
      companyId,
      projectId: t1
    };
    $[0] = companyId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const selfAccessProps = t2;
  const {
    onAccessChanged
  } = useSelfProjectAccess(selfAccessProps);
  const confirmRoleChange = useConfirmSelfRoleChange(selfAccessProps);
  let t3;
  if ($[3] !== companyId || $[4] !== confirmRoleChange || $[5] !== currentRole || $[6] !== dispatch || $[7] !== group || $[8] !== location || $[9] !== memberIdentity || $[10] !== onAccessChanged || $[11] !== openToast || $[12] !== selectedProject || $[13] !== subjectType || $[14] !== trackEvent) {
    const changeMemberRole = async function changeMemberRole(newRole) {
      if (newRole === currentRole) {
        return false;
      }
      if (subjectType === "project" && selectedProject) {
        return changeProjectRole(newRole);
      }
      if (subjectType === "group" && group) {
        await changeGroupRole(newRole);
        return true;
      }
      if (subjectType === "workspace" || subjectType === "team") {
        await changeCompanyRole(newRole);
        return true;
      }
      return false;
    };
    async function changeProjectRole(role) {
      const accessChange = {
        memberIdentity,
        currentRole,
        newRole: role
      };
      if (!(await confirmRoleChange(accessChange))) {
        return false;
      }
      trackEvent({
        name: ProjectMemberEvents.changeRole,
        props: {
          role,
          location
        }
      });
      await dispatch(updateMemberRoleInProjectSingle({
        companyId,
        projectId: selectedProject.id,
        userIdentity: memberIdentity,
        payload: {
          role
        }
      })).unwrap();
      openToast({
        title: "Role Changed",
        message: <RoleChangeSuccessDescription role={formatUserRoleType(role)} />
      });
      onAccessChanged(accessChange);
      return true;
    }
    async function changeGroupRole(role_0) {
      trackEvent({
        name: GroupMemberEvents.changeRole,
        props: {
          role: role_0,
          location
        }
      });
      await dispatch(updateMemberRoleInGroup({
        companyId,
        groupId: group.id,
        role: role_0,
        userIdentity: memberIdentity
      })).unwrap();
      openToast({
        title: "Role Changed",
        message: <RoleChangeSuccessDescription role={formatUserRoleType(role_0)} />
      });
    }
    async function changeCompanyRole(role_1) {
      const result = await dispatch(updateMemberRoleInWorkspace({
        companyId,
        role: role_1,
        identity: memberIdentity
      })).unwrap();
      if (result.role !== role_1) {
        const newRoleName = formatUserRoleType(result.role);
        const roleSubject = result.role === "COMPANY_MANAGER" || result.role === "PROJECT_MANAGER" ? "group" : "team";
        openToast({
          title: <>Role Changed to <strong>{newRoleName}</strong></>,
          message: <RolePartiallyChanged intendedRole={formatUserRoleType(role_1)} newRole={newRoleName} roleSubject={roleSubject} />,
          variant: "warning"
        });
      } else {
        openToast({
          title: "Role Changed",
          message: <RoleChangeSuccessDescription role={formatUserRoleType(role_1)} />
        });
      }
    }
    t3 = {
      changeMemberRole
    };
    $[3] = companyId;
    $[4] = confirmRoleChange;
    $[5] = currentRole;
    $[6] = dispatch;
    $[7] = group;
    $[8] = location;
    $[9] = memberIdentity;
    $[10] = onAccessChanged;
    $[11] = openToast;
    $[12] = selectedProject;
    $[13] = subjectType;
    $[14] = trackEvent;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
}